import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';

import {
    Colours,
    IconGarbage,
    DeleteConfirmationBlock,
    CircleSpinner,
    MessagePropType,
    AnimatedFadeContainer,
    useIsMounted,
    AnimatedExpandingContainer,
} from '@ratehub/base-ui';

import SuccessBlock from './SuccessBlock';
import ErrorBanner from './ErrorBanner';


function AnimatedDeleteContainer({
    children,

    onDelete,
    onDeleteComplete,

    canDelete,
    isItemDeleted,
    showErrorMessage,
    shouldShowSpinner,

    confirmationTitle,
    confirmText,
    rejectText,
    successTitle,
    errorTitle,

    className,
    
    ...otherProps
}) {
    const [ showConfirmation, setShowConfirmation ] = useState(false);
    const [ canShowDeleteSuccess, setCanShowDeleteSuccess ] = useState(false);

    const getIsMounted = useIsMounted();

    useEffect(
        () => {
            if (isItemDeleted) {
                setCanShowDeleteSuccess(true);
                setShowConfirmation(false);
            }
        },
        [ isItemDeleted ],
    );

    function handleDeleteClick() {
        setShowConfirmation(true);
    }

    function handleDeleteCancel() {
        setShowConfirmation(false);
    }

    // Adding a timeout here so we have 2 seconds to
    //  show the success message before we inform
    //  the consumer that they can actually unmount this component
    function handleDeleteAnimationCompletion() {
        if (canShowDeleteSuccess) {
            setTimeout(() => {
                onDeleteComplete?.();

                // After a time out we can hide the success message
                if (getIsMounted()) {
                    setCanShowDeleteSuccess(false);
                }
            }, 2000);
        }
    }

    return (
        <div 
            className={classNames(className, 'rh-position-relative')} 
            {...otherProps}
        >
            {/* When item is deleted, we want to fade out the children so only the success banner exists */}
            <AnimatedExpandingContainer 
                isVisible={!isItemDeleted} 
                alwaysRenderChildMarkup={true}
            >
                {children}
            </AnimatedExpandingContainer>
                        
            <If condition={canDelete}>
                <button
                    className="delete-button rh-position-absolute rh-top-0 rh-right-0 rh-display-flex rh-icon-xs rh-p-0"
                    type="button"
                    onClick={handleDeleteClick}
                    data-name="Dashboard-deleteButton"
                >
                    <IconGarbage
                        className="icon-garbage"
                        width="100%"
                        height="100%"
                    />
                </button>

                <AnimatedFadeContainer
                    isVisible={showConfirmation}
                    onEnd={handleDeleteAnimationCompletion}
                    duration={300}
                >
                    <DeleteConfirmationBlock
                        className="rh-position-absolute rh-top-0 rh-bottom-0 rh-left-0 rh-right-0"
                        titleMessage={confirmationTitle}
                        confirmMessage={confirmText}
                        rejectMessage={rejectText}
                        onConfirmClick={onDelete}
                        onRejectClick={handleDeleteCancel}
                        size="small"
                    />
                    <If condition={showErrorMessage}>
                        <ErrorBanner
                            className="rh-position-absolute rh-top-0 rh-left-0 rh-right-0"
                            title={errorTitle}
                        />
                    </If>
                </AnimatedFadeContainer>

                <If condition={shouldShowSpinner}>
                    <CircleSpinner />
                </If>
            </If>

            <If condition={isItemDeleted && canShowDeleteSuccess}>
                <SuccessBlock
                    className="rh-position-absolute rh-top-0 rh-left-0 rh-right-0 rh-zindex-elements"
                    title={successTitle}
                />
            </If>
        </div>
    );
}

AnimatedDeleteContainer.propTypes = {
    onDelete: PropTypes.func.isRequired,
    onDeleteComplete: PropTypes.func,
    children: PropTypes.node.isRequired,
    canDelete: PropTypes.bool,
    confirmationTitle: MessagePropType.isRequired,
    confirmText: MessagePropType.isRequired,
    rejectText: MessagePropType.isRequired,
    successTitle: PropTypes.string.isRequired,
    errorTitle: PropTypes.string.isRequired,
    shouldShowSpinner: PropTypes.bool,
    isItemDeleted: PropTypes.bool,
    showErrorMessage: PropTypes.bool,
    className: PropTypes.string,
};

AnimatedDeleteContainer.defaultProps = {
    onDeleteComplete: undefined,
    canDelete: true,
    shouldShowSpinner: false,
    isItemDeleted: false,
    showErrorMessage: false,
    className: undefined,
};


const StyledAnimatedDeleteContainer = styled(AnimatedDeleteContainer)`
    > .delete-button {
        align-content: flex-start;
        justify-content: center;
        
        border: none;
        outline: none;
        background: transparent;

        cursor: pointer;

        &:hover,
        &:focus {
            .icon-garbage {
                stroke: ${Colours.STRAWBERRY};
            }
        }
    }
`;

export default StyledAnimatedDeleteContainer;